import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useClickOutSide } from "lib/hooks/useClickOutside";

const LOCALE_OPTIONS = [
  { value: "ko-KR", labelKey: "localeSwitcher.ko" },
  { value: "en-US", labelKey: "localeSwitcher.en" },
  { value: "vi-VN", labelKey: "localeSwitcher.vi" },
  { value: "ja-JP", labelKey: "localeSwitcher.ja" },
  { value: "zh-CN", labelKey: "localeSwitcher.zh" },
];

function FlagIcon({ locale }) {
  const flagByLocale = {
    "ko-KR": "/images/korea_icon.png",
    "ja-JP": "/images/japan_icon.png",
    "en-US": "/images/us_icon.png",
    "zh-CN": "/images/china_icon.png",
    "vi-VN": "/images/vietnam_icon.png",
  };
  const src = flagByLocale[locale] || flagByLocale["ko-KR"];
  return <img className="localeFlagImg" src={src} alt="" />;
}

export default function LocaleSwitcher() {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selected = useMemo(() => {
    const lang = i18n.resolvedLanguage || i18n.language || "ko-KR";
    if (lang.startsWith("ja")) return "ja-JP";
    if (lang.startsWith("en")) return "en-US";
    if (lang.startsWith("vi")) return "vi-VN";
    if (lang.startsWith("zh")) return "zh-CN";
    return "ko-KR";
  }, [i18n.language, i18n.resolvedLanguage]);

  const handleSelect = (value) => {
    i18n.changeLanguage(value);
    setIsOpen(false);
  };

  useClickOutSide([dropdownRef], () => {
    setIsOpen(false);
  });

  const selectedOption = useMemo(
    () =>
      LOCALE_OPTIONS.find((option) => option.value === selected) ??
      LOCALE_OPTIONS[0],
    [selected],
  );

  return (
    <div className="headerLocaleSwitcher" aria-label="locale-switcher">
      <label>{t("localeSwitcher.label")}</label>
      <div className="localeSelectWrap" ref={dropdownRef}>
        <button
          type="button"
          className="localeDropdownButton"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className="localeFlag" aria-hidden="true">
            <FlagIcon locale={selectedOption.value} />
          </span>
          <span className="localeText">
            {selectedOption.label ?? t(selectedOption.labelKey)}
          </span>
          <span
            className={`localeArrow ${isOpen ? "open" : ""}`}
            aria-hidden="true"
          >
            ▽
          </span>
        </button>

        {isOpen && (
          <ul className="localeDropdownMenu" role="listbox">
            {LOCALE_OPTIONS.map((option) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === selected}
              >
                <button
                  type="button"
                  className={`localeDropdownItem ${option.value === selected ? "active" : ""}`}
                  onClick={() => handleSelect(option.value)}
                >
                  <span className="localeFlag" aria-hidden="true">
                    <FlagIcon locale={option.value} />
                  </span>
                  <span className="localeText">
                    {option.label ?? t(option.labelKey)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <select
          id="locale-select"
          value={selected}
          onChange={(event) => handleSelect(event.target.value)}
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: "none" }}
        >
          {LOCALE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label ?? t(option.labelKey)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
